import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import { Injectable } from "@angular/core";
import { KeycloakAuthGuard, KeycloakService } from "keycloak-angular";
import { from, Observable } from "rxjs";
import { KeycloakProfile } from "keycloak-js";
import { RoleEnum } from "../models/role.enum";

@Injectable({
  providedIn: "root"
})
export class AuthGuard extends KeycloakAuthGuard {
  constructor(
    protected override readonly router: Router,
    protected readonly keycloak: KeycloakService
  ) {
    super(router, keycloak);
  }

  async isAccessAllowed(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    if (!this.authenticated) {
      await this.keycloak.login({
        redirectUri: window.location.origin + state.url
      });
    }

    // If route specifies required roles, enforce them
    const requiredRoles: string[] | undefined = route.data?.["requiredRoles"] as
      | string[]
      | undefined;
    if (requiredRoles && requiredRoles.length > 0) {
      const hasAllRequired: boolean = requiredRoles.every((role: string): boolean =>
        this.roles?.includes(role)
      );
      if (!hasAllRequired) {
        // Redirect users without access to the all-referrals component
        return this.router.createUrlTree(["/admin", "all-referrals"]);
      }
    }

    return this.authenticated;
  }

  fetchUsername(): Observable<string | null> {
    return from(
      this.keycloakAngular.loadUserProfile().then((profile: KeycloakProfile): string | null => {
        return profile.username != null ? profile.username : null;
      })
    );
  }

  isBridgeUser(): boolean {
    return (
      this.roles?.includes(RoleEnum.ROLE_COMPANY_BRIDGE) &&
      !this.roles?.includes(RoleEnum.ROLE_COMPANY_AGENTIUM)
    );
  }

  isAgentiumUser(): boolean {
    return this.roles?.includes(RoleEnum.ROLE_COMPANY_AGENTIUM);
  }

  isProducer(): boolean {
    return this.roles?.includes(RoleEnum.ROLE_CONSOLE_PRODUCER);
  }

  isAdmin(): boolean {
    return this.roles?.includes(RoleEnum.ROLE_CONSOLE_ADMIN);
  }

  isAgencyAdmin(): boolean {
    return this.roles?.includes(RoleEnum.ROLE_CONSOLE_AGENCY_ADMIN);
  }

  isCarrier(): boolean {
    return this.roles?.includes(RoleEnum.ROLE_CONSOLE_CARRIER);
  }

  getThemeClass(): string {
    if (this.isAgentiumUser()) {
      return "agentium-theme";
    } else if (this.isBridgeUser()) {
      return "bridge-theme";
    }

    return "bridge-theme";
  }

  getThemeMarketplace(): number {
    if (this.isAgentiumUser()) {
      return 1;
    } else if (this.isBridgeUser()) {
      return 2;
    }

    return 1;
  }

  logout(): void {
    this.keycloakAngular.logout(window.location.origin + "/admin").finally();
  }
}
